import React from "react";
import { Link } from "react-router-dom";
import { Atom, TableProperties, FlaskConical, MessageSquare, ClipboardCheck, ArrowRight, Sparkles, GraduationCap } from "lucide-react";

const modules = [
  { to: "/curriculum", title: "3-Year IB Syllabus", desc: "Slovak bilingual curriculum · 173 lessons with AI-generated explainers.", icon: GraduationCap, color: "cyan", tid: "home-card-curriculum" },
  { to: "/table", title: "Periodic Table", desc: "Explore all 118 elements — atomic mass, electron config, uses.", icon: TableProperties, color: "cyan", tid: "home-card-table" },
  { to: "/topics", title: "Concept Guides", desc: "Bite-sized lessons on atoms, bonding, reactions, stoichiometry & more.", icon: Atom, color: "violet", tid: "home-card-topics" },
  { to: "/quiz", title: "Practice Quizzes", desc: "Test yourself with instant feedback and explanations.", icon: ClipboardCheck, color: "lime", tid: "home-card-quiz" },
  { to: "/tutor", title: "AI Chemistry Tutor", desc: "Ask anything. Powered by Claude. Explanations tailored for you.", icon: MessageSquare, color: "amber", tid: "home-card-tutor" },
  { to: "/balancer", title: "Equation Balancer", desc: "Type any unbalanced equation, get the answer instantly.", icon: FlaskConical, color: "cyan", tid: "home-card-balancer" },
];

const colorMap = {
  cyan: { border: "border-cyan-400/30", accent: "text-cyan-300", glow: "hover:shadow-[0_0_28px_rgba(0,229,255,0.25)]", top: "border-t-cyan-400" },
  violet: { border: "border-fuchsia-400/30", accent: "text-fuchsia-300", glow: "hover:shadow-[0_0_28px_rgba(176,38,255,0.25)]", top: "border-t-fuchsia-400" },
  lime: { border: "border-lime-400/30", accent: "text-lime-300", glow: "hover:shadow-[0_0_28px_rgba(57,255,20,0.20)]", top: "border-t-lime-400" },
  amber: { border: "border-amber-400/30", accent: "text-amber-300", glow: "hover:shadow-[0_0_28px_rgba(251,191,36,0.20)]", top: "border-t-amber-400" },
};

const HERO_BG = "url(https://images.unsplash.com/photo-1579567761406-4684ee0c75b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMDNkJTIwbW9sZWN1bGUlMjBkYXJrfGVufDB8fHx8MTc4Njg1NTk1NHww&ixlib=rb-4.1.0&q=85)";
const STATS = [{ k: "Elements", v: "118" }, { k: "Topics", v: "8" }, { k: "Quiz Qs", v: "40+" }, { k: "AI Tutor", v: "Live" }];

function Hero() {
  return (
    <section className="relative overflow-hidden rounded-md border border-white/10 bg-black/40 p-10 md:p-14">
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: HERO_BG, backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "screen" }} />
      <div className="relative max-w-3xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs uppercase tracking-widest text-cyan-300"><Sparkles size={12} /> High School Chemistry</div>
        <h1 className="font-display mt-6 text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
          Understand chemistry <br />like a <span className="text-cyan-400">scientist</span>, not a textbook.
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">Every element, every reaction, every trick — with an AI tutor that patiently answers whatever you throw at it.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/table" data-testid="hero-explore-btn" className="inline-flex items-center gap-2 rounded-md bg-cyan-500 px-5 py-3 text-sm font-semibold text-black transition-colors hover:bg-cyan-400">Explore periodic table <ArrowRight size={16} /></Link>
          <Link to="/tutor" data-testid="hero-tutor-btn" className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-slate-100 transition-colors hover:bg-white/10">Ask the tutor <MessageSquare size={16} /></Link>
        </div>
      </div>
    </section>
  );
}

function ModuleCard({ m, span }) {
  const c = colorMap[m.color];
  return (
    <Link to={m.to} data-testid={m.tid} className={`group relative overflow-hidden rounded-md border ${c.border} border-t-2 ${c.top} bg-black/40 p-6 transition-transform duration-150 ease-out ${c.glow} hover:-translate-y-0.5 ${span}`}>
      <div className="mb-6 flex items-center justify-between">
        <div className={`grid h-10 w-10 place-items-center rounded-md border ${c.border} ${c.accent}`}><m.icon size={18} /></div>
        <ArrowRight size={16} className="text-slate-500 transition-transform duration-150 group-hover:translate-x-1 group-hover:text-slate-200" />
      </div>
      <h3 className="font-display text-xl font-semibold text-white">{m.title}</h3>
      <p className="mt-2 text-sm text-slate-400">{m.desc}</p>
    </Link>
  );
}

function ModuleGrid() {
  return (
    <section>
      <div className="mb-8 flex items-end justify-between">
        <h2 className="font-display text-2xl font-bold sm:text-3xl">Where do you want to start?</h2>
        <span className="hidden font-mono-num text-xs uppercase tracking-widest text-slate-500 md:block">/modules</span>
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        {modules.map((m, i) => <ModuleCard key={m.to} m={m} span={i < 2 ? "md:col-span-6" : "md:col-span-4"} />)}
      </div>
    </section>
  );
}

function StatsBar() {
  return (
    <section className="grid grid-cols-2 gap-4 rounded-md border border-white/10 bg-black/40 p-6 md:grid-cols-4" data-testid="home-stats">
      {STATS.map((s) => (
        <div key={s.k} className="text-center">
          <div className="font-mono-num text-2xl font-semibold text-cyan-300 sm:text-3xl">{s.v}</div>
          <div className="mt-1 text-xs uppercase tracking-widest text-slate-500">{s.k}</div>
        </div>
      ))}
    </section>
  );
}

export default function Home() {
  return (
    <div className="space-y-14" data-testid="home-root">
      <Hero />
      <ModuleGrid />
      <StatsBar />
    </div>
  );
}
